import React, { useEffect, useMemo, useState } from "react";
import MetaTags from "react-meta-tags";
import axios from "@axiosConfig";
import RespondologyLogo from "components/ui/RespondologyLogo";
import { Colors } from "styles";

const SECURITY_EMAIL = "security@respondology.com";
const SECURITY_EMAIL_MAILTO = `mailto:${SECURITY_EMAIL}`;

const STATUS_DETAILS = {
  pending: {
    label: "pending",
    description: "Your request has been received and is awaiting further action.",
  },
  in_progress: {
    label: "in progress",
    description: "The process for deleting your data has been started.",
  },
  completed: {
    label: "completed",
    description: "Your data has been deleted.",
  },
  failed: {
    label: "failed",
    description: (
      <>
        Something has gone wrong. Please contact our Security and Compliance
        team at <a href={SECURITY_EMAIL_MAILTO}>{SECURITY_EMAIL}</a> for
        assistance.
      </>
    ),
    color: Colors.danger,
  },
};

const DeletionRequestStatus = ({ match }) => {
  const {
    params: { code },
  } = match;
  const [status, setStatus] = useState();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isSubscribed = true;

    const fetchStatus = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`/deletion_requests/${code}`);
        const statusFromResponse = data && data.status;
        const normalizedStatus =
          typeof statusFromResponse === "string"
            ? statusFromResponse.toLowerCase()
            : undefined;

        if (normalizedStatus && STATUS_DETAILS[normalizedStatus]) {
          if (isSubscribed) {
            setStatus(normalizedStatus);
            setError(undefined);
          }
        } else if (isSubscribed) {
          setError("We couldn't determine the status of your request.");
          setStatus(undefined);
        }
      } catch (requestError) {
        if (isSubscribed) {
          const responseStatus =
            requestError &&
            requestError.response &&
            requestError.response.status;
          if (responseStatus === 404) {
            setError(
              <>
                We couldn't find a deletion request for that confirmation code.
                Please double-check the link or contact our Security and
                Compliance team at <a href={SECURITY_EMAIL_MAILTO}>{SECURITY_EMAIL}</a> for help.
              </>
            );
          } else {
            setError(
              "We couldn't load your request status. Please try again later."
            );
          }
          setStatus(undefined);
        }
      } finally {
        if (isSubscribed) {
          setLoading(false);
        }
      }
    };

    fetchStatus();

    return () => {
      isSubscribed = false;
    };
  }, [code]);

  const currentStatusDetails = useMemo(() => {
    if (!status) {
      return undefined;
    }

    return STATUS_DETAILS[status];
  }, [status]);

  return (
    <div className="page">
      <MetaTags>
        <title>Respondology: Deletion Request Status</title>
        <meta
          id="Deletion Request Status"
          name="description"
          content="Check the status of your Respondology deletion request."
        />
      </MetaTags>
      <div className="card">
        <div className="card-logo">
          <RespondologyLogo />
        </div>
        <main>
          <h1>Deletion Request Status</h1>
          {loading && <p className="info">Loading your request status…</p>}
          {!loading && error && <p className="error">{error}</p>}
          {!loading && !error && currentStatusDetails && (
            <p className="status-text">
              Your request is <span>{currentStatusDetails.label}.</span>
              <div>{currentStatusDetails.description}</div>
            </p>
          )}
          <p className="contact-info">
            If you need assistance with your request, please contact our
            Security and Compliance team at {" "}
            <a href={SECURITY_EMAIL_MAILTO}>{SECURITY_EMAIL}</a>.
          </p>
        </main>
      </div>

      <style jsx>{`
        .page {
          min-height: 100vh;
          background: ${Colors.gray000};
          display: flex;
          justify-content: center;
          align-items: flex-start;
          padding: 40px 20px;
        }

        .card {
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
          max-width: 640px;
          width: 100%;
          padding: 40px 32px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .card-logo {
          width: 100%;
          display: flex;
          justify-content: center;
          margin-bottom: 24px;
        }

        main {
          display: flex;
          flex-direction: column;
          gap: 24px;
          width: 100%;
        }

        h1 {
          font-size: 28px;
          color: ${Colors.gray700};
          margin: 0;
          text-align: center;
        }

        h2 {
          font-size: 20px;
          color: ${Colors.gray700};
          margin: 0 0 16px;
          text-align: left;
        }

        .info {
          color: ${Colors.gray500};
          text-align: center;
          margin: 0;
        }

        .error {
          color: ${Colors.danger};
          text-align: center;
          margin: 0;
        }

        .status-text {
          font-size: 22px;
          color: ${Colors.gray700};
          text-align: center;
          margin: 0;
        }

        .status-text span {
          color: ${
            (currentStatusDetails && currentStatusDetails.color) ||
            Colors.gray700
          };
          font-weight: 600;
        }

        .contact-info {
          margin: 0;
          color: ${Colors.gray500};
          text-align: center;
          line-height: 1.5;
        }

        .status-explanations {
          background: ${Colors.gray100};
          border-radius: 12px;
          padding: 24px;
        }

        dl {
          margin: 0;
          display: grid;
          grid-template-columns: 1fr;
          row-gap: 16px;
        }

        .status-item {
          display: grid;
          grid-template-columns: minmax(120px, 160px) 1fr;
          column-gap: 16px;
          align-items: start;
        }

        dt {
          font-weight: 600;
          text-transform: capitalize;
        }

        dd {
          margin: 0;
          color: ${Colors.gray500};
          line-height: 1.5;
        }

        @media (max-width: 600px) {
          .status-item {
            grid-template-columns: 1fr;
            row-gap: 4px;
          }

          dt {
            text-align: left;
          }
        }
      `}</style>
    </div>
  );
};

export default DeletionRequestStatus;
